import { ReactNode, useEffect, useState } from 'react'

import { useDeviceDrawerData } from '../../hooks/useDeviceDrawerData'
import { useDarkDeviceDrawerTabs } from '../hooks/useDarkDeviceDrawerTabs'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useTranslation } from 'react-i18next'

import { Box, Drawer, Tabs } from '@hivewatch/dark'

import { DeviceType } from '@/graphql/generated/schemas'
import { DEVICE_DRAWER_Z_INDEX } from '@/styles/theme'

import { DeviceLabelsSection } from '../../components/DeviceLabelsSection'
import { DarkDeviceDrawerHeader } from './DarkDeviceDrawerHeader'

type DeviceDrawerData = ReturnType<typeof useDeviceDrawerData>

interface IProps {
  deviceId: string
  isOpen: boolean
  onClose: () => void
  /** Opens the edit-device modal. The caller builds the device payload. */
  onEdit: () => void
  defaultIndex: number
  deviceType: DeviceType
  device: DeviceDrawerData['device']
  incidents: DeviceDrawerData['incidents']
  isIncidentsLoading: boolean
  isLoading: boolean
  /** Video region for this device type. Kept legacy for Phase 1 (deferred). */
  videoSlot: ReactNode
}

/**
 * Reskinned device drawer (rebrand — ENG-32123, Phase 1). Renders the shared
 * dark `Drawer` (ENG-32086) with a custom `header` slot and the shared chrome
 * (header + labels). The video region is supplied by the type-specific wrapper
 * via `videoSlot`; the tab bar and per-tab content remain the current
 * implementation pending later phases.
 */
export const DarkDeviceDrawer = ({
  deviceId,
  isOpen,
  onClose,
  onEdit,
  defaultIndex,
  deviceType,
  device,
  incidents,
  isIncidentsLoading,
  isLoading,
  videoSlot,
}: IProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const prefix = colorScheme === 'light' ? 'light.' : ''
  const { shouldEnableUpdateDevice } = usePermissions()
  const isDeviceLabelingEnabled = useFeatureFlag(FeatureFlag.deviceLabeling)

  const [index, setIndex] = useState(defaultIndex)

  useEffect(() => {
    setIndex(defaultIndex)
  }, [defaultIndex])

  const tabs = useDarkDeviceDrawerTabs({
    incidents,
    isIncidentsLoading,
    deviceId,
    deviceFloorId: device?.floor?.id,
    deviceName: device?.name,
    deviceType,
  })

  return (
    <Drawer
      // Falls back to a stable label so the dialog always has an accessible
      // name — the device name isn't available until data loads.
      ariaLabel={device?.name || t('devices:drawer_aria_label')}
      // Content sections own their own padding; drop the Drawer body's default.
      bodyStyleOverrides={{ px: '0', py: '0' }}
      colorScheme={colorScheme}
      header={
        <DarkDeviceDrawerHeader
          canEdit={shouldEnableUpdateDevice}
          colorScheme={colorScheme}
          device={device}
          deviceType={deviceType}
          isLoading={isLoading}
          onClose={onClose}
          onEdit={onEdit}
        />
      }
      isOpen={isOpen}
      onClose={onClose}
      // Device drawer sits on the `card` surface rather than the shared
      // Drawer's default elevated surface.
      panelStyleOverrides={{ bg: `${prefix}bg.card` }}
      width='625px'
      zIndex={DEVICE_DRAWER_Z_INDEX}
    >
      <Box pt='24px' px='24px'>
        {videoSlot}
      </Box>
      {isDeviceLabelingEnabled && (
        <DeviceLabelsSection
          deviceId={deviceId}
          deviceTags={device?.tags?.edges?.map((e) => e.node) ?? []}
          editable={shouldEnableUpdateDevice}
        />
      )}
      <Box pt='20px' px='24px'>
        <Tabs
          activeId={tabs[index]?.label ?? ''}
          colorScheme={colorScheme}
          fontFamily='Soehne'
          items={tabs.map((tab) => ({
            id: tab.label,
            label: tab.label,
            icon: tab.icon,
          }))}
          onChange={(id) => {
            const newIndex = tabs.findIndex((tab) => tab.label === id)
            if (newIndex !== -1) setIndex(newIndex)
          }}
        />
      </Box>
      <Box>{tabs[index]?.panel}</Box>
    </Drawer>
  )
}
